// Detail side-car for the ScoredCallsBrowser. Fetches the call's
// full section/measure/criteria breakdown from the CH-backed
// /breakdown endpoint and renders the hierarchy via CallScoreBreakdown.
// Falls back to the row-level aggregate when the fetch is forbidden
// (cross-org admin without a support window).

import React, { useEffect, useState } from 'react'
import { isNil } from 'lodash'
import { useDispatch } from 'react-redux'

import { IconX } from '@tabler/icons-react'

import { fetchingAPI, apiService } from '@/api'
import { ExternalLink } from '@/components/ExternalLink'
import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'

import { CALL_EXPLORER_TAB_ENUMS } from '@/views/Calls/components/helpers'

import { CallScoreBreakdown } from './CallScoreBreakdown'
import { formatTimestamp, pctFrom, StatePill } from '../helpers/format'

export const ScoredCallDetailPanel = ({ call, orgId, agentName, scorecardName, onClose }) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorKind, setErrorKind] = useState(null)

  const callId = call?.call_id
  const callSid = call?.sid
  useEffect(() => {
    if (!callId || !orgId) {
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setErrorKind(null)
    const params = new URLSearchParams()
    params.set('requested_organization_id', orgId)
    if (callSid) {
      params.append('sids', callSid)
    }
    fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/calls/${callId}/breakdown?${params.toString()}`,
      'GET',
      dispatch
    )
      .then((result) => {
        if (!cancelled) {
          setDetail(result)
        }
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const status = err?.status || err?.statusCode || err?.response?.status
        setErrorKind(status === 403 ? 'forbidden' : 'error')
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [callId, orgId, callSid])

  const items = Array.isArray(detail?.items) ? detail.items : []
  const matching = items.find((item) => item?.sid === call?.sid) ?? items[0]
  const rowPct = call?.possible_score
    ? Math.round((call.aggregate_score / call.possible_score) * 100)
    : null
  const detailPct = matching ? pctFrom(matching.aggregate_score, matching.possible_score) : null
  const rowAggregate = !isNil(rowPct) ? `${rowPct}%` : '—'
  const shownAggregate = !isNil(detailPct) ? `${Math.round(detailPct)}%` : rowAggregate

  return (
    <div className="scored-call-detail">
      <div className="scored-call-detail-header">
        <div>
          <div className="scored-call-detail-title">
            {agentName || `Agent ${call?.user_id || ''}`}
          </div>
          <div className="scored-call-detail-subtitle">
            {formatTimestamp(call?.start_time)}
            {scorecardName && <> · {scorecardName}</>}
          </div>
        </div>
        <Button basic square small onClick={onClose} aria-label="Close call detail">
          <IconX />
        </Button>
      </div>

      {loading && <Loader centered />}

      {!loading && (
        <div className="scored-call-detail-aggregate">
          Aggregate score: <strong>{shownAggregate}</strong>
          {matching?.auto_failed && <StatePill kind="auto-fail" />}
        </div>
      )}

      {!loading && errorKind === 'forbidden' && (
        <div className="entity-drawer-empty">
          You don&apos;t have permission to view this call&apos;s full breakdown (cross-org access
          requires a support window). Open in Call Explorer if you have access there.
        </div>
      )}
      {!loading && errorKind === 'error' && (
        <div className="entity-drawer-error">Couldn&apos;t load this call.</div>
      )}
      {!loading && !errorKind && !matching && (
        <div className="entity-drawer-empty">No score data for this call.</div>
      )}
      {!loading && !errorKind && matching && <CallScoreBreakdown scoreData={matching} />}
      {!loading && (
        <div className="scored-call-detail-actions">
          {/* Deep-link straight to the QA Scores tab -- this is a scored
              call, so land the coach on the scorecard, not the summary. */}
          <ExternalLink
            url={`../calls/${callId}?tab=${CALL_EXPLORER_TAB_ENUMS.QA_SCORES}`}
            label="View scores in Call Explorer"
            id={callId}
          />
        </div>
      )}
    </div>
  )
}
